import { FormParticipantTitles, NotificationIdentifier } from '@evorait/pace-core';
import {
    CheckpointDependencies,
    Checkpoints,
    initialCheckpointDependencies,
    initialCheckpoints,
} from '../../types/checkpoints';
import { ContentBlocks, initialContentBlocksState } from '../../types/content-blocks';
import { Copyright, initialCopyrightState } from '../../types/copyright';
import { FormFilters, initialFormFilters } from '../../types/form-filter';
import { FormIdentifier, initialFormIdentifier } from '../../types/form-identifier';
import { FormParticipantCopies } from '../../types/form-participant-copies';
import { FormTypeFilters, initialFormTypeFilters } from '../../types/form-type-filters';
import { FormsConstants } from '../../types/forms-constants';
import { Headings, initialHeadings } from '../../types/headings';
import { HintsForFilling } from '../../types/hints-for-filling';
import { initialOptionDependencies, OptionDependencies } from '../../types/option';
import { initialOrgDataState, OrgData } from '../../types/org-data';
import { initialParticipantCopyState, ParticipantCopyId } from '../../types/participant-copy';
import { initialParticipantCopyModalState, ParticipantCopyModal } from '../../types/participant-copy-modal';
import { RadioButtonLabels } from '../../types/radio-button-labels';
import { Recipient } from '../../types/recipient';
import { SystemCondition } from '../../types/system-condition';
import { initialNotificationIdentifier } from './notification-identifier.state';
import { ManuallyIndividualizedComponents } from '../../types/individualization-component';
import { MeasuringEquipments } from '../../types/measuring-equipments';

export type FormState = Readonly<{
    // form defining properties
    formId: FormIdentifier;
    notification: NotificationIdentifier;
    type: FormsConstants.FORMTYPE;
    infoClass: FormsConstants.INFOCLASS;

    // pre-filled from the participant list of the notification
    participantTitles: FormParticipantTitles;

    // form submit process relevant
    saveMode: FormsConstants.LOAD_STATUS;
    lockedBy: string;
    deviceId: string;
    lockedOn: string; // date + time formatted according ISO 8601
    recipients: Recipient[];

    // UI controlling properties
    isLoading: boolean;
    errorDuringLoading: boolean; // will be set if the form could not be loaded, eg. due to a parsing error
    isSaving: boolean;
    readOnlyReason: FormsConstants.READONLY_REASON;
    radioButtonLabels: RadioButtonLabels;
    typeFilters: FormTypeFilters; // only filters that have matching checkpoints will be there
    formFilters: FormFilters;
    supportParticipantCopies: boolean; // enables or disables UI elements relevant for participant copy workflow
    participantCopyModal: ParticipantCopyModal;

    // static form content
    doctype: string | undefined; // only used in Astoria
    title: string;
    variant: string;
    language: string;
    orgData: OrgData;
    copyright: Copyright;
    instructions?: string; // only used in Astoria
    headings: Headings;
    content: ContentBlocks;
    checkpoints: Checkpoints;
    checkpointDependencies: CheckpointDependencies;
    optionDependencies: OptionDependencies;

    loadingStatus: FormsConstants.FORM_LOADING_STATUS;
    // editable elements of the form
    systemCondition?: SystemCondition;
    hintsForFilling?: HintsForFilling;
    participantCopies: FormParticipantCopies;
    // selected participant with id 'master' means the form master is displayed
    selectedParticipantCopyId: ParticipantCopyId;
    accordionGroups: Record<string, boolean>;
    escalationEmail: string;
    enableManualIndividualization: boolean;
    materialKey?: string;
    manIndividualizationComponents?: ManuallyIndividualizedComponents[];
    individualization?: boolean;
    measuringEquipments?: MeasuringEquipments;
}>;

export const initialFormState: FormState = {
    // form defining properties
    formId: initialFormIdentifier,
    notification: initialNotificationIdentifier,
    type: FormsConstants.FORMTYPE.UNKNOWN,
    infoClass: FormsConstants.INFOCLASS.UNKNOWN,

    // pre-filled from the participant list of the notification
    participantTitles: {},

    // form submit process relevant
    saveMode: FormsConstants.LOAD_STATUS.INTERIM,
    lockedBy: '',
    deviceId: '',
    lockedOn: '',
    recipients: [],

    // UI controlling properties
    isLoading: false,
    errorDuringLoading: false,
    isSaving: false,
    readOnlyReason: FormsConstants.READONLY_REASON.NONE,
    radioButtonLabels: {
        yes: FormsConstants.RADIOBUTTON_LABELS.YES,
        no: FormsConstants.RADIOBUTTON_LABELS.NO,
        na: FormsConstants.RADIOBUTTON_LABELS.NA,
    },
    typeFilters: initialFormTypeFilters,
    formFilters: initialFormFilters,
    supportParticipantCopies: false,
    participantCopyModal: initialParticipantCopyModalState,

    // static form content
    doctype: undefined,
    title: '',
    variant: '',
    language: '',
    orgData: initialOrgDataState,
    copyright: initialCopyrightState,
    instructions: undefined,
    headings: initialHeadings,
    content: initialContentBlocksState,
    checkpoints: initialCheckpoints,
    checkpointDependencies: initialCheckpointDependencies,
    optionDependencies: initialOptionDependencies,

    // editable elements of the form
    systemCondition: undefined,
    hintsForFilling: undefined,
    participantCopies: {
        [FormsConstants.MASTER_PARTICIPANT_ID]: {
            ...initialParticipantCopyState,
            id: FormsConstants.MASTER_PARTICIPANT_ID,
        },
    },
    selectedParticipantCopyId: FormsConstants.MASTER_PARTICIPANT_ID,
    accordionGroups: {},
    escalationEmail: '',
    loadingStatus: FormsConstants.FORM_LOADING_STATUS.UNLOADED,
    enableManualIndividualization: false,
    manIndividualizationComponents: [],
    measuringEquipments: undefined,
};
